import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { facebookPageService } from '@/services/instagramService'
import type { CatalogProductInput } from '@/services/instagramService'
import { queryKeys } from '../keys'

/** Apps the user manages on Meta (`manage_app_solution`). */
export function useManagedAppsQuery() {
  return useQuery({
    queryKey: queryKeys.meta.managedApps(),
    queryFn: () => facebookPageService.listManagedApps(),
  })
}

export function useBusinessesQuery() {
  return useQuery({
    queryKey: queryKeys.meta.businesses(),
    queryFn: () => facebookPageService.listBusinesses(),
  })
}

export function useCatalogsQuery(businessId: string) {
  return useQuery({
    queryKey: queryKeys.meta.catalogs(businessId),
    queryFn: () => facebookPageService.listCatalogs(businessId),
    enabled: !!businessId,
  })
}

export function useCreateCatalog(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => facebookPageService.createCatalog(businessId, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.meta.catalogs(businessId) }),
  })
}

export function useCatalogProductsQuery(catalogId: string) {
  return useQuery({
    queryKey: queryKeys.meta.catalogProducts(catalogId),
    queryFn: () => facebookPageService.listCatalogProducts(catalogId),
    enabled: !!catalogId,
  })
}

/** Add or remove a product, then reload the catalog's list. */
export function useCatalogProductMutations(catalogId: string) {
  const queryClient = useQueryClient()
  const reload = () => queryClient.invalidateQueries({ queryKey: queryKeys.meta.catalogProducts(catalogId) })
  return {
    create: useMutation({
      mutationFn: (product: CatalogProductInput) => facebookPageService.createCatalogProduct(catalogId, product),
      onSuccess: reload,
    }),
    remove: useMutation({
      mutationFn: (productId: string) => facebookPageService.deleteCatalogProduct(productId),
      onSuccess: reload,
    }),
  }
}
